import React, { useState, useRef, useMemo, useEffect, useLayoutEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Phone, CreditCard,
  ChevronLeft, ChevronRight, Star, ArrowRight,
  Wine, Beer, GlassWater, Sparkles, Gift,
  ShieldCheck, Truck, MessageSquareText
} from 'lucide-react';
import { Container } from '@/components/layout/container';
import { FloatingActions } from '@/components/layout/floating-actions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { getActiveOffers, parseOffers } from '@/features/offers/offers';
import { ProductCard, ProductCardSkeleton } from '@/features/products/components/product-card';
import { useFeaturedProducts, useProducts, useCategories } from '@/features/products/hooks/use-products';
import { useProductFilterStore } from '@/features/products/stores/product-filters';
import { useCart } from '@/features/cart/hooks/use-cart';
import { useCartMutations } from '@/features/cart/hooks/use-cart-mutations';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { useSettings } from '@/features/settings/hooks/use-settings';
import { useBranches } from '@/features/branches/hooks/use-branches';
import { FindUsSection } from '@/features/branches/components/find-us-section';
import type { ProductDisplay, ProductVariant } from '@/features/products/types';

const BANNER_LAYOUTS = {
  compact: {
    aspectRatio: '16 / 5',
    maxWidthClass: 'max-w-6xl',
    copy: 'Compact strip',
  },
  balanced: {
    aspectRatio: '16 / 6',
    maxWidthClass: 'max-w-7xl',
    copy: 'Balanced hero',
  },
  cinematic: {
    aspectRatio: '2 / 1',
    maxWidthClass: 'max-w-[88rem]',
    copy: 'Cinematic showcase',
  },
} as const;

// Category icons mapping
const CATEGORY_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  'Whiskey': GlassWater,
  'Whisky': GlassWater,
  'Wine': Wine,
  'Beer': Beer,
  'Vodka': Sparkles,
  'Gin': GlassWater,
  'Rum': GlassWater,
  'Brandy': Wine,
  'Champagne': Wine,
  'Tequila': GlassWater,
  '_default': Gift,
};

// Sample testimonials


// Promotional deals


/**
 * Alternative Homepage V2
 * Testing ground for new homepage design
 */
export function HomePageV2() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { settings } = useSettings();
  const setCategory = useProductFilterStore((state) => state.setCategory);
  const setSearch = useProductFilterStore((state) => state.setSearch);
  const resetFilters = useProductFilterStore((state) => state.resetFilters);
  const { data: branches, isLoading: isBranchesLoading } = useBranches();
  
  // Use featured products hook for efficient fetching (only gets featured products, not all)
  const { data: featuredData, isLoading: isFeaturedLoading, error: featuredError } = useFeaturedProducts(8);
  const { data: allProducts, isLoading: isAllProductsLoading, error: allProductsError } = useProducts({ perPage: 50 });
  const { data: categoriesData, isLoading: isCategoriesLoading } = useCategories();
  
  const { items: cartItems } = useCart(user?.id);
  const { toggleCartProduct } = useCartMutations(user?.id);
  

  
  // Carousel state
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [nowTick, setNowTick] = useState(() => new Date());

  // Carousel settings - already parsed by use-settings hook
  const carouselImages = settings.carousel_images || [];
  const carouselEnabled = settings.carousel_enabled;
  const carouselAutoplay = settings.carousel_autoplay;
  const carouselInterval = typeof settings.carousel_interval === 'number' ? settings.carousel_interval : 5000;

  // Auto-advance carousel
  useEffect(() => {
    if (!carouselEnabled || carouselImages.length === 0 || !carouselAutoplay) return;
    const interval = setInterval(() => {
      setCarouselIndex(prev => (prev + 1) % carouselImages.length);
    }, carouselInterval);
    return () => clearInterval(interval);
  }, [carouselEnabled, carouselImages.length, carouselAutoplay, carouselInterval]);

  const jumboEnabled = settings.jumbo_image_enabled;

  // Update offer timing checks (timed offers)
  useEffect(() => {
    const id = window.setInterval(() => setNowTick(new Date()), 30_000);
    return () => window.clearInterval(id);
  }, []);
  
  const newArrivalsRef = useRef<HTMLDivElement>(null);
  const jumboFrameRef = useRef<HTMLDivElement>(null);
  const jumboImageRef = useRef<HTMLImageElement>(null);

  // Parse promotions from settings
  const promotions = useMemo(() => parseOffers(settings.promotions), [settings.promotions]);
  const activePromotions = useMemo(
    () => getActiveOffers(promotions, nowTick, settings.offers_timezone),
    [promotions, nowTick, settings.offers_timezone]
  );

  // Parse testimonials from settings
  const testimonials = useMemo(() => {
    try {
      const parsed = typeof settings.testimonials === 'string' 
        ? JSON.parse(settings.testimonials) 
        : settings.testimonials;
      return (parsed || []) as Array<{id: string; name: string; rating: number; text: string; date: string}>;
    } catch {
      return [];
    }
  }, [settings.testimonials]);

  const featuredProducts = featuredData || [];
  const newArrivals = allProducts?.data?.filter(p => p.isNewArrival).slice(0, 8) || [];
  const onSaleProducts = allProducts?.data?.filter(p => p.isOnSale).slice(0, 4) || [];

  const handleAddToCart = async (product: ProductDisplay, variant?: ProductVariant) => {
    await toggleCartProduct({ product, quantity: 1, variant });
  };

  const handleCategoryClick = (categoryId: string) => {
    resetFilters();
    setCategory(categoryId);
    navigate('/catalog');
  };

  const scrollCarousel = (ref: React.RefObject<HTMLDivElement>, direction: 'left' | 'right') => {
    if (ref.current) {
      const scrollAmount = 300;
      ref.current.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
    }
  };


  const displayCategories = categoriesData || [];
  const displayBranch = branches?.find(b => b.is_main) || branches?.[0];

  // Banner - only show if banner_url is set in settings (no fallback to local image)
  const bannerUrl = settings.banner_url;
  const bannerAlt = settings.banner_alt || 'Karebe Wines & Spirits';
  const bannerFit = settings.banner_fit || 'cover';
  const bannerLayout = settings.banner_layout || 'balanced';
  const bannerFrame = BANNER_LAYOUTS[bannerLayout];

  // Show banner only when settings are loaded and banner_url exists
  const showBanner = settings.banner_enabled && bannerUrl && bannerUrl.length > 0;

  // Priority: carousel > banner (only show one hero at top)
  const showCarousel = carouselEnabled && carouselImages.length > 0;
  const showJumbo = jumboEnabled && !!settings.jumbo_image_url && !showCarousel && !showBanner;

  // Jumbo parallax (disabled for reduced-motion users)
  useLayoutEffect(() => {
    if (!showJumbo) return;

    const frame = jumboFrameRef.current;
    const img = jumboImageRef.current;
    if (!frame || !img) return;

    const reduceMotionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (reduceMotionQuery?.matches) {
      img.style.transform = '';
      img.style.willChange = '';
      return;
    }

    const PARALLAX_PX = 56;
    const SCALE = 1.08;
    let rafId = 0;
    let isVisible = true;

    const applyTransform = () => {
      const rect = frame.getBoundingClientRect();
      const viewport = window.innerHeight || 0;
      if (viewport <= 0) return;

      // 0 when element enters from bottom, 1 when it exits at top
      const progress = (viewport - rect.top) / (viewport + rect.height);
      const clamped = Math.max(0, Math.min(1, progress));
      const translateY = (clamped - 0.5) * 2 * PARALLAX_PX;

      img.style.transform = `translate3d(0, ${translateY.toFixed(2)}px, 0) scale(${SCALE})`;
      img.style.willChange = 'transform';
    };

    const schedule = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        if (!isVisible) return;
        applyTransform();
      });
    };

    const onScroll = () => schedule();
    const onResize = () => schedule();

    const io =
      typeof IntersectionObserver !== 'undefined'
        ? new IntersectionObserver(
            (entries) => {
              const entry = entries[0];
              isVisible = Boolean(entry?.isIntersecting);
              if (isVisible) schedule();
            },
            { root: null, threshold: 0, rootMargin: '200px 0px 200px 0px' }
          )
        : null;

    io?.observe(frame);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    applyTransform();

    const onReduceMotionChange = () => {
      if (reduceMotionQuery?.matches) {
        img.style.transform = '';
        img.style.willChange = '';
      } else {
        schedule();
      }
    };

    reduceMotionQuery?.addEventListener?.('change', onReduceMotionChange);

    return () => {
      io?.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      if (rafId) window.cancelAnimationFrame(rafId);
      reduceMotionQuery?.removeEventListener?.('change', onReduceMotionChange);
    };
  }, [showJumbo]);

  const submitCatalogSearch = (raw: string) => {
    const term = raw.trim();
    resetFilters();
    setSearch(term);
    navigate('/catalog');
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,#fff6e8_0%,#fffaf2_32%,#f8f3ea_64%,#f2ede4_100%)] pb-28 sm:pb-32">
      {/* Hero Section - Banner or Carousel - Full viewport width, edge to edge */}
      {showCarousel ? (
        // Carousel - Full width
        <div className="w-full h-64 md:h-80 lg:h-96 overflow-hidden relative -mt-4 md:-mt-8">
          <img 
            src={carouselImages[carouselIndex]?.url} 
            alt={carouselImages[carouselIndex]?.alt || 'Carousel image'} 
            className="w-full h-full object-cover"
          />
          {/* Carousel controls */}
          {carouselImages.length > 1 && (
            <>
              <button 
                onClick={() => setCarouselIndex(prev => (prev - 1 + carouselImages.length) % carouselImages.length)}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                aria-label="Previous slide"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button 
                onClick={() => setCarouselIndex(prev => (prev + 1) % carouselImages.length)}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                aria-label="Next slide"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
              {/* Dots indicator */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {carouselImages.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCarouselIndex(idx)}
                    className={`w-3 h-3 rounded-full transition-colors ${
                      idx === carouselIndex ? 'bg-white' : 'bg-white/50'
                    }`}
                    aria-label={`Go to slide ${idx + 1}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      ) : showBanner ? (
        // Banner Image - Framed hero with configurable proportions
        <div className="px-4 pt-4 md:px-6 lg:px-8">
          <div className={`mx-auto ${bannerFrame.maxWidthClass}`}>
            <div
              className="overflow-hidden rounded-2xl border border-gray-200 bg-white"
              style={{ aspectRatio: bannerFrame.aspectRatio }}
            >
              <img
                src={bannerUrl}
                alt={bannerAlt}
                className="h-full w-full"
                style={{ objectFit: bannerFit }}
              />
            </div>
            <div className="mt-3 flex items-center justify-between px-1 text-xs text-brand-700/80">
              <span>Homepage banner</span>
              <span>{bannerFrame.copy}</span>
            </div>
          </div>
        </div>
      ) : null}

      {/* Jumbo Image - fallback hero when no carousel/banner */}
      {showJumbo && (
        <div className="px-4 pt-4 md:px-6 lg:px-8">
          <div className="mx-auto max-w-[88rem]">
            <div
              ref={jumboFrameRef}
              className="h-56 overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm sm:h-64 md:h-80 lg:h-[420px]"
            >
              <img
                ref={jumboImageRef}
                src={settings.jumbo_image_url}
                alt="Jumbo hero image"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </div>
      )}

      {/* Quick catalog search */}
      <Container>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submitCatalogSearch(catalogSearch);
          }}
          className="relative z-10"
        >
          <div
            className={`mx-auto max-w-3xl ${
              showCarousel || showBanner || showJumbo ? '-mt-10 md:-mt-12' : 'mt-4'
            }`}
          >
            <div className="flex items-center gap-2 rounded-2xl border border-gray-200 bg-white/90 p-2 shadow-lg backdrop-blur">
              <Input
                value={catalogSearch}
                onChange={(e) => setCatalogSearch(e.target.value)}
                placeholder="Search drinks (e.g. Jameson, Tusker, Glenfiddich...)"
                aria-label="Search products"
                className="h-12 border-0 bg-transparent text-base shadow-none focus-visible:ring-0"
              />
              <Button type="submit" size="lg" className="h-12 rounded-xl px-6">
                Search
              </Button>
            </div>
            <div className="mt-2 text-center text-xs text-brand-700/80">
              Tip: press Enter to jump to the catalog.
            </div>
          </div>
        </form>
      </Container>

      {/* Contact Info Bar */}
      <div className="bg-brand-800 text-white px-4 py-3 mt-8 mb-6 flex flex-wrap items-center justify-center gap-6 shadow-md">
        {(() => {
          if (displayBranch) {
            return (
              <div className="flex flex-col items-center sm:flex-row gap-2 sm:gap-6 border-b sm:border-b-0 border-white/20 pb-2 sm:pb-0 last:border-0 last:pb-0">
                <div className="font-bold text-brand-200 uppercase text-xs sm:text-sm tracking-wider flex items-center gap-1">
                  {displayBranch.name} {displayBranch.is_main && <span className="bg-brand-600 text-white px-1.5 py-0.5 rounded text-[10px]">(Main)</span>}
                </div>
                <div className="flex items-center gap-4">
                  {displayBranch.phone && (
                    <div className="flex items-center gap-1.5 opacity-90 hover:opacity-100 transition-opacity">
                      <Phone className="w-4 h-4" />
                      <a href={`tel:${displayBranch.phone.replace(/\s+/g, '')}`} className="font-medium text-sm hover:underline">{displayBranch.phone}</a>
                    </div>
                  )}
                  {displayBranch.mpesa_shortcode && (
                    <div className="flex items-center gap-1.5 opacity-90 hover:opacity-100 transition-opacity bg-brand-900/40 px-2 py-1 rounded-md">
                      <CreditCard className="w-4 h-4" />
                      <span className="font-medium text-sm">Till: {displayBranch.mpesa_shortcode}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          }
          
          return (
            <>
              <div className="flex items-center gap-2">
                <Phone className="w-5 h-5" />
                <span className="font-medium">+254 724 721627</span>
              </div>
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                <span className="font-medium">Till: 9137883</span>
              </div>
            </>
          );
        })()}
      </div>

      <Container>
        {/* Hero Section */}
        <section className="py-8">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-display font-bold text-brand-900 mb-2">
              Welcome to Karebe Wines & Spirits
            </h1>
            <p className="text-brand-600 text-lg">
              Kenya's finest selection of premium wines and spirits
            </p>
            <div className="flex justify-center gap-4 mt-4">
              <Button size="lg" onClick={() => document.getElementById('deals')?.scrollIntoView({ behavior: 'smooth' })}>
                View Deals
              </Button>
              <Button variant="outline" size="lg" onClick={() => navigate('/catalog')}>
                Browse Catalog
              </Button>
            </div>
          </div>

          {/* Featured Products */}
          {isFeaturedLoading ? (
            <div id="featured-products" className="mt-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-display font-bold text-brand-900">Featured Products</h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <ProductCardSkeleton key={i} />
                ))}
              </div>
            </div>
          ) : featuredError ? (
            <div className="mt-8 text-center py-8">
              <p className="text-red-500">Failed to load featured products. Please try again.</p>
            </div>
          ) : featuredProducts.length > 0 ? (
            <div id="featured-products" className="mt-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-display font-bold text-brand-900">Featured Products</h2>
                <Link to="/catalog" className="text-brand-600 hover:text-brand-700 flex items-center gap-1">
                  View All <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {featuredProducts.slice(0, 4).map((product) => (
                  <ProductCard key={product.id} product={product} onAddToCart={handleAddToCart} />
                ))}
              </div>
            </div>
          ) : null}
        </section>

        {/* Category Navigation */}
        <section className="border-t border-brand-100 py-10">
          <h2 className="text-2xl font-display font-bold text-brand-900 text-center mb-6">Shop by Category</h2>
          {isCategoriesLoading ? (
            <div className="grid grid-cols-4 md:grid-cols-8 gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="flex flex-col items-center justify-center p-4 bg-white rounded-xl shadow-sm border border-brand-100">
                  <div className="w-12 h-12 rounded-full bg-brand-100 flex items-center justify-center mb-2 animate-pulse" />
                  <div className="h-4 w-16 bg-brand-100 rounded animate-pulse" />
                </div>
              ))}
            </div>
          ) : displayCategories.length > 0 ? (
            <div className="grid grid-cols-4 md:grid-cols-8 gap-4">
              {displayCategories.slice(0, 8).map((category) => {
                const IconComponent = CATEGORY_ICONS[category.name] || CATEGORY_ICONS._default;
                return (
                  <button
                    key={category.id}
                    onClick={() => handleCategoryClick(category.id)}
                    className="flex flex-col items-center justify-center rounded-2xl border border-brand-100 bg-white/85 p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
                  >
                    <div className="w-12 h-12 rounded-full bg-brand-100 flex items-center justify-center mb-2">
                      <IconComponent className="w-6 h-6 text-brand-600" />
                    </div>
                    <span className="text-sm font-medium text-brand-900 text-center">{category.name}</span>
                  </button>
                );
              })}
              <button
                onClick={() => navigate('/catalog')}
                className="flex flex-col items-center justify-center rounded-2xl border border-brand-200 bg-brand-50/90 p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
              >
                <div className="w-12 h-12 rounded-full bg-brand-200 flex items-center justify-center mb-2">
                  <span className="text-2xl">+</span>
                </div>
                <span className="text-sm font-medium text-brand-700 text-center">More</span>
              </button>
            </div>
          ) : (
            <div className="text-center py-8 text-brand-500">
              <p>No categories available.</p>
            </div>
          )}
        </section>

        {/* Deals Section */}
        {activePromotions.length > 0 && (
          <section id="deals" className="border-t border-brand-100 py-10">
            <h2 className="text-2xl font-display font-bold text-brand-900 text-center mb-6">
              Special Deals & Promotions
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {activePromotions.map((promo) => (
                <Card
                  key={promo.id}
                  className="overflow-hidden border-white/80 bg-white/90 shadow-[0_24px_60px_-50px_rgba(76,29,8,0.45)]"
                  role={promo.link ? 'link' : undefined}
                  onClick={() => promo.link && navigate(promo.link)}
                >
                  <div className={`h-2 bg-gradient-to-r ${promo.bgColor || 'from-amber-500 to-orange-600'}`} />
                  <CardContent className="p-6">
                    {promo.discount ? (
                      <Badge variant="warning" className="mb-2">
                        {promo.discount} OFF
                      </Badge>
                    ) : null}
                    <h3 className="text-xl font-bold text-brand-900 mb-2">{promo.title}</h3>
                    {promo.description ? <p className="text-brand-600 mb-4">{promo.description}</p> : null}
                    <div className="flex items-center justify-between gap-3">
                      {promo.code ? (
                        <code className="bg-brand-100 px-3 py-1 rounded text-sm font-mono">{promo.code}</code>
                      ) : (
                        <span />
                      )}
                      {promo.endsOn ? (
                        <span className="text-xs text-brand-500">
                          Valid until {new Date(promo.endsOn).toLocaleDateString()}
                        </span>
                      ) : null}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

          {/* On Sale Products */}
          {isAllProductsLoading ? (
            <div className="mt-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-display font-bold text-brand-900">On Sale Now</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <ProductCardSkeleton key={i} />
                ))}
              </div>
            </div>
          ) : allProductsError ? (
            <div className="mt-8 text-center py-8">
              <p className="text-red-500">Failed to load sale items. Please try again.</p>
            </div>
          ) : onSaleProducts.length > 0 ? (
            <div className="mt-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-display font-bold text-brand-900">On Sale Now</h3>
                <Link to="/catalog?filter=on_sale" className="text-brand-600 hover:text-brand-700 flex items-center gap-1">
                  View All <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {onSaleProducts.map((product) => (
                  <ProductCard key={product.id} product={product} onAddToCart={handleAddToCart} />
                ))}
              </div>
            </div>
          ) : null}
          </section>
        )}

        {/* New Arrivals Carousel */}
        <section className="border-t border-brand-100 py-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-display font-bold text-brand-900">New Arrivals</h2>
            <div className="flex gap-2">
              <Button variant="outline" size="icon" onClick={() => scrollCarousel(newArrivalsRef, 'left')}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="icon" onClick={() => scrollCarousel(newArrivalsRef, 'right')}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {isAllProductsLoading ? (
            <div className="flex gap-4 overflow-x-auto scrollbar-hide pb-4" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex-shrink-0 w-64">
                  <ProductCardSkeleton />
                </div>
              ))}
            </div>
          ) : newArrivals.length > 0 ? (
            <div ref={newArrivalsRef} className="flex gap-4 overflow-x-auto scrollbar-hide pb-4 snap-x" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {newArrivals.map((product) => (
                <div key={product.id} className="flex-shrink-0 w-64 snap-start">
                  <ProductCard product={product} onAddToCart={handleAddToCart} />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-brand-500">
              <p>No new arrivals yet. Check back soon!</p>
            </div>
          )}
        </section>

        {/* Testimonials */}
        <section className="border-t border-brand-100 py-10">
          <h2 className="text-2xl font-display font-bold text-brand-900 text-center mb-6">What Our Customers Say</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial: {id: string; name: string; rating: number; text: string; date: string}) => (
              <Card key={testimonial.id} className="border-white/80 bg-white/88 shadow-[0_24px_60px_-50px_rgba(76,29,8,0.42)]">
                <CardContent className="p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className={`w-4 h-4 ${i < testimonial.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                    ))}
                  </div>
                  <p className="text-brand-700 mb-4 italic">"{testimonial.text}"</p>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-brand-900">{testimonial.name}</span>
                    <span className="text-sm text-brand-500">{new Date(testimonial.date).toLocaleDateString()}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Trust Signals */}
        <section className="border-t border-brand-100 py-10">
          <div className="grid md:grid-cols-4 gap-6 text-center">
            <div className="rounded-2xl border border-white/70 bg-white/75 p-5 shadow-sm">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-brand-100">
                <ShieldCheck className="h-6 w-6 text-brand-700" />
              </div>
              <h3 className="font-semibold text-brand-900 mb-1">100% Authentic</h3>
              <p className="text-sm text-brand-600">Genuine products guaranteed</p>
            </div>
            <div className="rounded-2xl border border-white/70 bg-white/75 p-5 shadow-sm">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-brand-100">
                <Truck className="h-6 w-6 text-brand-700" />
              </div>
              <h3 className="font-semibold text-brand-900 mb-1">Fast Delivery</h3>
              <p className="text-sm text-brand-600">Same-day delivery available</p>
            </div>
            <div className="rounded-2xl border border-white/70 bg-white/75 p-5 shadow-sm">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-brand-100">
                <CreditCard className="h-6 w-6 text-brand-700" />
              </div>
              <h3 className="font-semibold text-brand-900 mb-1">Best Prices</h3>
              <p className="text-sm text-brand-600">Price match guarantee</p>
            </div>
            <div className="rounded-2xl border border-white/70 bg-white/75 p-5 shadow-sm">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-brand-100">
                <MessageSquareText className="h-6 w-6 text-brand-700" />
              </div>
              <h3 className="font-semibold text-brand-900 mb-1">WhatsApp Ordering</h3>
              <p className="text-sm text-brand-600">Quick order via chat</p>
            </div>
          </div>
        </section>

        {/* Find Us - Branch Locations Map */}
        <FindUsSection 
          branches={branches ?? []} 
          isLoading={isBranchesLoading}
        />
      </Container>

      {/* Floating Actions */}
      <FloatingActions
        cartItemCount={cartItems.reduce((sum, item) => sum + item.quantity, 0)}
        isLoggedIn={!!user}
        userRole={user?.role}
        onAdminClick={() => navigate('/admin')}
        onRiderClick={() => navigate('/rider')}
        onLoginClick={() => navigate('/admin/login')}
      />
    </div>
  );
}

export default HomePageV2;
